import { Component, OnInit, OnDestroy } from '@angular/core';
import { PanierService } from '../services/panier.service';
import { FournisseurService } from '../services/fournisseur.service';
import { CdkTableColumns, CdkTableTypeColumn } from '../models/cdk-table.model';
import { HttpServices, QueryParameter } from '../services/http.service';
import { FournisseursModel, FournisseurType, ItemAcountRecherche, Panier, PanierState } from '../models';
import { AccountResult, ItemAccount } from '../models';
import * as config from '../config/config';
import { Right } from '../models/Rights.enum';
import { ReferenceService } from '../services/reference.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FilterCriteria } from '../models/filter-criteria.model';
import { RightsService } from '../services/rights.service';
import { LoaderService } from '../services/loader.service';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { TrackingService } from '../services/tracking.service';

@Component({
  selector: 'fs-balance-outstanding',
  styleUrls: ['./balance-outstanding.sass'],
  templateUrl: './balance-outstanding.component.html'
})

export class BalanceOutstandingComponent implements OnInit, OnDestroy {
  suppliersList: FournisseursModel[];
  activeSupplierId: number;
  rechercheItem = new ItemAcountRecherche();
  accountResult = new AccountResult();
  itemsPerPage = 20;
  listSizeItemsPerPage = [10, 20, 30, 40, 50];
  currentPage = 0;
  askedPage = 0;
  totalPages: any[];
  totalEntries = 0;
  isFirstAddToCart = true;
  Right = Right;
  columns: CdkTableColumns[] = [
    {
      title: '',
      name: 'Panier',
      sort: false,
      typeColumn: CdkTableTypeColumn.rowImage,
      requireReadRight: Right.Cart,
      readOnly: true
    },
    {
      title: 'N° pièce',
      responsiveTitle: 'N° pièce: ',
      name: 'DocumentNumber',
      sort: '',
      typeColumn: CdkTableTypeColumn.html,
      readOnly: true,
      requireReadRight: Right.Cart
    },
    {
      title: 'Émis',
      responsiveTitle: 'Émis: ',
      name: 'DateEmission',
      sort: '',
      typeColumn: CdkTableTypeColumn.date,
      readOnly: true,
      requireReadRight: Right.Cart
    },
    {
      title: 'Échéance',
      responsiveTitle: 'Échéance: ',
      name: 'DateEcheance',
      sort: 'asc',
      typeColumn: CdkTableTypeColumn.date,
      readOnly: true,
      requireReadRight: Right.Cart
    },
    {
      title: 'Montant',
      responsiveTitle: 'Montant: ',
      name: 'Montant',
      sort: '',
      typeColumn: CdkTableTypeColumn.money,
      readOnly: true,
      requireReadRight: Right.Cart
    },
    {
      title: 'Solde',
      responsiveTitle: 'Solde: ',
      name: 'Solde',
      sort: '',
      typeColumn: CdkTableTypeColumn.money,
      readOnly: true,
      requireReadRight: Right.Cart
    },
    {
      title: '',
      responsiveTitle: 'Télécharger le document: ',
      name: 'UrlPdf',
      sort: false,
      typeColumn: CdkTableTypeColumn.imageLink
    }
  ];

  displayedColumns: string[] = this.columns.map(x => x.name);
  isAllInCart = false;
  hasMultiSuppliers: boolean;
  cartContent: Panier;
  cartContentChangeSub: Subscription;
  cartAmount: number;
  payDisabled: boolean;
  hasPayRight: boolean;
  rowsInitialized = false;
  displayPaymentMsg = false;
  paymentMsgHeader;
  paymentMsgBody;
  paymentMsgClass;
  cartState: PanierState;

  constructor(
    private httpServices: HttpServices,
    private fournisseurService: FournisseurService,
    private panierService: PanierService,
    private referenceService: ReferenceService,
    private activatedRoute: ActivatedRoute,
    private rightsService: RightsService,
    private loaderService: LoaderService,
    private router: Router,
    private trackingService: TrackingService
  ) {
    this.cartContentChangeSub = this.panierService.cartContentChange$.subscribe(panier => {
      this.updateCartContent(panier);
    });
  }

  ngOnInit() {
    this.activatedRoute.data.subscribe(resolvedData => {
      this.cartState = this.panierService.getCartState();
      if (this.cartState === PanierState.PAY_SUCCESS ||
        this.cartState === PanierState.PAY_ERROR) {
        this.setPaymentMsg(this.cartState);
      }

      if (this.rightsService.hasAccessRights(Right.Cart)) {
        if (this.cartState === PanierState.PAY_SUCCESS) {
          this.fournisseurService.initSuppliersInfos(resolvedData.balanceOutstandingInfos[0]);
          this.cartContent = Panier.extractPanier(resolvedData.balanceOutstandingInfos[1]);
        } else {
          this.cartContent = Panier.extractPanier(resolvedData.balanceOutstandingInfos);
        }
        this.cartAmount = this.cartContent.TotalPanier.Total;
        this.panierService.initCart(this.cartContent);
        this.panierService.setCartState(PanierState.BILLS_CHOICE);
      }

      const colTrie = this.columns.find(col => col.sort === 'asc' || col.sort === 'desc');
      if (colTrie !== undefined) {
        this.calcRechercheTrie(colTrie);
      }

      this.suppliersList = this.fournisseurService.getSuppliersInfosList();
      this.activeSupplierId = this.fournisseurService.getActiveSupplierId();
      this.hasMultiSuppliers = this.fournisseurService.getSuppliersNb() > 1;
      this.hasPayRight = this.rightsService.hasReadWriteRights(this.Right.Payment);
      this.payDisabled = !this.hasPayRight || this.cartContent.DetailPanier.length === 0 || this.cartAmount <= 0;

      this.fetchUnpaidPieces();
      this.loaderService.decreaseLoaderCount();
    });
  }

  setPaymentMsg(cartState: PanierState): void {
    this.displayPaymentMsg = true;
    const supplier: FournisseurType = +this.activatedRoute.snapshot.queryParamMap.get('paymentSupplierId');
    switch (cartState) {
      case PanierState.PAY_SUCCESS:
        this.paymentMsgClass = 'success';
        this.paymentMsgHeader = 'Votre demande a bien été prise en compte !';
        this.paymentMsgBody = `Votre paiement auprès de ${FournisseurType[supplier]} est en cours de traitement.`;
        break;

      case PanierState.PAY_ERROR:
        this.paymentMsgClass = 'alert';
        this.paymentMsgHeader = 'Votre paiement n’a pu être pris en compte !';
        this.paymentMsgBody = `Il y a une erreur dans votre paiement auprès de ${FournisseurType[supplier]}.<br/>Votre panier n’est pas perdu ! Retentez votre paiement.`;
        break;

      default:
        break;
    }
  }

  updateCartContent(newContent: Panier): void {
    this.cartContent = newContent;
    this.cartAmount = newContent.TotalPanier.Total;
    this.selectPiecesInCart();
    this.checkIsAllInCart();
    this.payDisabled = !this.hasPayRight || this.cartContent.DetailPanier.length === 0 || this.cartAmount <= 0;
    this.loaderService.decreaseLoaderCount();
  }

  onCellClick($event: any) {
    const col: CdkTableColumns = $event.column;
    const item: ItemAccount = $event.row;
    if (col.name !== 'UrlPdf' && item.isEligible) {
      if (!item.IsInshoppingCart) {
        const piece = { BillId: item.BillId, IdPieceComptable: item.AccountingDocumentId };
        this.panierService.addPiecesToCart(this.activeSupplierId, [piece]);
        if (this.isFirstAddToCart) {
          this.isFirstAddToCart = false;
          this.trackingService.pushEvent('financial-online_payment-step1');
        }
      } else {
        this.panierService.removePieceFromCart(this.activeSupplierId, item.AccountingDocumentId);
      }
    }
  }

  fournisseurChange(supplierId: number) {
    this.loaderService.increaseLoaderCount();
    this.activeSupplierId = supplierId;
    this.fournisseurService.setActiveSupplierId(supplierId);
    this.askedPage = 0;
    const cartContent$ = this.panierService.fetchCart(this.activeSupplierId);
    this.rechercheItem.referenceItem = '';
    const piecesList$ = this.fetchUnpaidPiecesObs();
    forkJoin([cartContent$, piecesList$]).subscribe(response => {
      this.cartContent = Panier.extractPanier(response[0]);
      this.cartAmount = this.cartContent.TotalPanier.Total;
      this.panierService.initCart(this.cartContent);
      this.initList(response[1]);
      this.payDisabled = !this.hasPayRight || this.cartContent.DetailPanier.length === 0 || this.cartAmount <= 0;
      this.loaderService.decreaseLoaderCount();
    });
  }

  calcRechercheTrie(col: CdkTableColumns) {
    this.rechercheItem.trieItemAccount.id = col.name;
    this.rechercheItem.trieItemAsc = col.sort === 'asc';
  }

  onColumnClick(column: CdkTableColumns) {
    if (column.name === 'Panier') {
      if (!this.isAllInCart) {
        const piecesToAdd = [];
        this.accountResult.Pieces.filter(piece => !piece.IsInshoppingCart).forEach(piece => {
          piecesToAdd.push({ BillId: piece.BillId, IdPieceComptable: piece.AccountingDocumentId });
        });
        this.panierService.addPiecesToCart(this.activeSupplierId, piecesToAdd);
      } else {
        this.panierService.clearCart(this.activeSupplierId);
      }
    } else {
      this.calcRechercheTrie(column);
      this.fetchUnpaidPieces();
    }
  }

  checkIsAllInCart(): void {
    this.isAllInCart = this.accountResult.Pieces.every(piece => piece.IsInshoppingCart);
  }

  fetchUnpaidPieces(): void {
    this.loaderService.increaseLoaderCount();
    this.fetchUnpaidPiecesObs().subscribe(response => {
      this.initList(response);
      this.loaderService.decreaseLoaderCount();
    });
  }

  fetchUnpaidPiecesObs(): Observable<AccountResult> {
    const param = this.getObligatoryParam();
    this.addOptionalParams(param);

    return this.httpServices.get<AccountResult>(this.referenceService.staticReferences.Action[config.ApiKeys.GET_SOLDE_PAY_LIST],
      () => { }, param);
  }

  private getObligatoryParam(): QueryParameter[] {
    const params = [
      { 'Key': 'fournisseurId', 'Value': String(this.activeSupplierId) },
      { 'Key': 'pageNum', 'Value': String(this.askedPage) },
      { 'Key': 'pageSize', 'Value': String(this.itemsPerPage) }
    ];

    if (this.rechercheItem.trieItemAccount.id !== '') {
      params.push({
        'Key': 'orderBy',
        'Value': this.rechercheItem.trieItemAccount.id + (this.rechercheItem.trieItemAsc ? ' ASC' : ' DESC')
      });
    } else {
      params.push({ 'Key': 'orderBy', 'Value': 'DateEcheance' + ' ASC' });
    }
    return params;
  }

  private addOptionalParams(params: QueryParameter[]) {
    if (this.rechercheItem.referenceItem) {
      params.push({ 'Key': 'numPiece', 'Value': this.rechercheItem.referenceItem });
    }

    if (this.rechercheItem.DateDebut) {
      params.push({ 'Key': 'dateMin', 'Value': new Date(this.rechercheItem.DateDebut).toDateString() });
    }

    if (this.rechercheItem.DateFin) {
      params.push({ 'Key': 'dateMax', 'Value': new Date(this.rechercheItem.DateFin).toDateString() });
    }
  }

  private initList(response: AccountResult) {
    this.accountResult = AccountResult.extractData(response);
    this.currentPage = response.CurrentPage;
    this.totalPages = new Array(response.TotalPages + 1);
    this.totalEntries = response.TotalEntries;
    this.selectPiecesInCart();
    this.checkIsAllInCart();
    this.rowsInitialized = true;
  }

  private selectPiecesInCart(): void {
    this.accountResult.Pieces.forEach(
      piece => piece.IsInshoppingCart = this.isInCart(piece)
    );
  }

  private isInCart(piece: ItemAccount): boolean {
    return this.cartContent.DetailPanier.findIndex(item => item.IdPieceComptable === piece.AccountingDocumentId) !== -1;
  }

  changePage(page: number) {
    this.askedPage = page;
  }

  onChangeSizePage(sizePage: number): void {
    this.itemsPerPage = sizePage;
    this.askedPage = 0;
    this.fetchUnpaidPieces();
  }

  filterPieces(filterCriteria: FilterCriteria) {
    this.rechercheItem.referenceItem = filterCriteria.numPiece;
    this.rechercheItem.DateDebut = filterCriteria.dateDebut;
    this.rechercheItem.DateFin = filterCriteria.dateFin;
    this.askedPage = 0;
    this.fetchUnpaidPieces();
  }

  goToSelectPay(): void {
    this.panierService.setCartState(PanierState.PAY_MEAN_CHOICE);
    this.router.navigate(['/select-pay', this.activeSupplierId]);
  }

  ngOnDestroy() {
    this.cartContentChangeSub.unsubscribe();
  }
}
